import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { communitiesApi, ticketsApi } from '../api';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import {
  Loader2, AlertCircle, Plus, X, Filter, LifeBuoy, MessageSquare, Send,
  Clock, CheckCircle, RefreshCw, ChevronRight, ChevronLeft,
} from 'lucide-react';

const STATUS_LABELS = {
  open: { label: 'Otwarty', color: 'bg-amber-100 text-amber-700', icon: Clock },
  in_progress: { label: 'W toku', color: 'bg-blue-100 text-blue-700', icon: RefreshCw },
  resolved: { label: 'Rozwiązany', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  closed: { label: 'Zamknięty', color: 'bg-slate-100 text-slate-600', icon: CheckCircle },
};

const CATEGORY_LABELS = {
  question: 'Pytanie',
  bug: 'Błąd / Problem',
  request: 'Prośba',
  problem: 'Problem',
  other: 'Inne',
};

const PRIORITY_LABELS = {
  low: { label: 'Niski', color: 'bg-slate-100 text-slate-600' },
  normal: { label: 'Normalny', color: 'bg-slate-100 text-slate-700' },
  high: { label: 'Wysoki', color: 'bg-orange-100 text-orange-700' },
  urgent: { label: 'Pilny', color: 'bg-rose-100 text-rose-700' },
};

/**
 * Tickety = prywatne zgłoszenia w community (pytania, błędy, prośby).
 * Widoczność: zgłaszający + assignee + owner/admin/moderator.
 * Status flow: open → in_progress → resolved → closed.
 */
export default function MyTicketsPage() {
  const [searchParams] = useSearchParams();
  const initialTicketId = searchParams.get('ticket_id');
  const { user } = useAuth();
  const [community, setCommunity] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [selected, setSelected] = useState(null);   // ticket detail
  const [creatingOpen, setCreatingOpen] = useState(false);

  const loadCommunity = async () => {
    try {
      const r = await communitiesApi.mine();
      setCommunity(r.data);
      return r.data;
    } catch (e) {
      if (e?.response?.status === 404) setError('Brak Twojej społeczności w Aurabase.');
      else setError(e?.response?.data?.detail || e.message);
      return null;
    }
  };

  const loadTickets = async (communityId) => {
    try {
      const opts = filterStatus ? { status: filterStatus } : {};
      const r = await ticketsApi.list(communityId, opts);
      setTickets(r.data?.items || []);
      setIsAdmin(!!r.data?.is_admin);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      const c = await loadCommunity();
      if (c?.id) {
        await loadTickets(c.id);
        if (initialTicketId) {
          try {
            const r = await ticketsApi.get(initialTicketId);
            setSelected(r.data);
          } catch {}
        }
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (community?.id) loadTickets(community.id);
  }, [filterStatus]);

  const handleSelect = async (t) => {
    try {
      const r = await ticketsApi.get(t.id);
      setSelected(r.data);
    } catch (e) { alert('Błąd: ' + (e?.response?.data?.detail || e.message)); }
  };

  const refreshSelected = async () => {
    if (!selected?.ticket?.id) return;
    const r = await ticketsApi.get(selected.ticket.id);
    setSelected(r.data);
    if (community?.id) await loadTickets(community.id);
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div></DashboardLayout>;
  if (error) return (
    <DashboardLayout>
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="bg-white border border-rose-200 rounded-lg p-6 max-w-md text-center">
          <AlertCircle className="w-10 h-10 mx-auto mb-3 text-rose-500" />
          <p className="text-sm text-rose-600">{error}</p>
        </div>
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <LifeBuoy className="w-6 h-6 text-rose-500" /> Tickety {community ? `— ${community.name}` : ''}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Prywatne zgłoszenia: pytania, błędy, prośby. Widoczne tylko dla Ciebie i adminów społeczności.
          </p>
        </div>
        <button
          onClick={() => setCreatingOpen(true)}
          className="flex items-center gap-1 px-3 py-2 bg-rose-600 text-white text-sm rounded hover:bg-rose-700"
        >
          <Plus className="w-4 h-4" /> Nowy ticket
        </button>
      </div>

      <div className="flex items-center gap-2 mb-3 text-sm">
        <Filter className="w-4 h-4 text-slate-400" />
        {['', 'open', 'in_progress', 'resolved', 'closed'].map(s => (
          <button
            key={s}
            onClick={() => setFilterStatus(s)}
            className={`px-2 py-1 rounded text-xs ${filterStatus === s ? 'bg-rose-100 text-rose-700' : 'text-slate-500 hover:bg-slate-100'}`}
          >
            {s === '' ? 'Wszystkie' : STATUS_LABELS[s]?.label}
          </button>
        ))}
        {isAdmin && <span className="ml-auto text-[11px] text-slate-500">Widzisz wszystkie tickety (admin)</span>}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div>
          {tickets.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-lg p-8 text-center text-sm text-slate-500">
              <LifeBuoy className="w-10 h-10 mx-auto mb-2 opacity-30" />
              {filterStatus ? 'Brak ticketów w tym statusie.' : 'Brak ticketów. Kliknij "Nowy ticket" aby zacząć.'}
            </div>
          ) : (
            <div className="space-y-2">
              {tickets.map(t => {
                const sb = STATUS_LABELS[t.status] || STATUS_LABELS.open;
                const SbIcon = sb.icon;
                const pb = PRIORITY_LABELS[t.priority] || PRIORITY_LABELS.normal;
                const isSelected = selected?.ticket?.id === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => handleSelect(t)}
                    className={`w-full text-left p-3 rounded-lg border transition ${isSelected ? 'bg-rose-50 border-rose-300' : 'bg-white border-slate-200 hover:border-rose-200'}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <h3 className="font-medium text-slate-900 truncate flex-1">{t.title}</h3>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1 ${sb.color}`}>
                        <SbIcon className="w-3 h-3" /> {sb.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-slate-500 flex-wrap">
                      <span>{CATEGORY_LABELS[t.category] || t.category}</span>
                      <span className={`px-1.5 py-0.5 rounded ${pb.color}`}>{pb.label}</span>
                      {t.comments_count > 0 && <span className="flex items-center gap-0.5"><MessageSquare className="w-3 h-3" /> {t.comments_count}</span>}
                      <span className="ml-auto">{new Date(t.created_at).toLocaleDateString('pl-PL')}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div>
          {selected ? (
            <TicketDetail
              data={selected}
              isAdmin={selected.is_admin}
              currentUserId={user?.id}
              onClose={() => setSelected(null)}
              onChanged={refreshSelected}
            />
          ) : (
            <div className="bg-white border border-dashed border-slate-300 rounded-lg p-8 text-center text-sm text-slate-500 hidden lg:block">
              <ChevronLeft className="w-8 h-8 mx-auto mb-2 opacity-30" />
              Wybierz ticket z listy żeby zobaczyć szczegóły.
            </div>
          )}
        </div>
      </div>

      {creatingOpen && community && (
        <CreateTicketModal
          communityId={community.id}
          onClose={() => setCreatingOpen(false)}
          onCreated={async () => {
            setCreatingOpen(false);
            if (community?.id) await loadTickets(community.id);
          }}
        />
      )}
    </DashboardLayout>
  );
}

function TicketDetail({ data, isAdmin, currentUserId, onClose, onChanged }) {
  const ticket = data.ticket;
  const comments = data.comments || [];
  const sb = STATUS_LABELS[ticket.status] || STATUS_LABELS.open;
  const SbIcon = sb.icon;
  const pb = PRIORITY_LABELS[ticket.priority] || PRIORITY_LABELS.normal;
  const [newComment, setNewComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const isAuthor = ticket.author_id === currentUserId;

  const submit = async (e) => {
    e?.preventDefault();
    if (!newComment.trim() || sending) return;
    setSending(true);
    try {
      await ticketsApi.addComment(ticket.id, { content: newComment.trim(), is_internal: isInternal });
      setNewComment(''); setIsInternal(false);
      await onChanged();
    } catch (err) {
      alert('Komentarz: ' + (err.response?.data?.detail || err.message));
    } finally { setSending(false); }
  };

  const changeStatus = async (newStatus) => {
    try {
      await ticketsApi.update(ticket.id, { status: newStatus });
      await onChanged();
    } catch (err) {
      alert('Status: ' + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-start justify-between gap-2 mb-3">
        <h2 className="font-semibold text-slate-900 flex-1">{ticket.title}</h2>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
      </div>

      <div className="flex items-center gap-2 text-xs flex-wrap mb-3">
        <span className={`px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1 ${sb.color}`}>
          <SbIcon className="w-3 h-3" /> {sb.label}
        </span>
        <span className={`px-2 py-0.5 rounded ${pb.color}`}>{pb.label}</span>
        <span className="text-slate-500">{CATEGORY_LABELS[ticket.category] || ticket.category}</span>
        <span className="text-slate-400 ml-auto">{new Date(ticket.created_at).toLocaleString('pl-PL')}</span>
      </div>

      {isAdmin && (
        <div className="flex items-center gap-1 mb-3 text-xs flex-wrap p-2 bg-slate-50 rounded">
          <span className="text-slate-500 mr-1">Zmień status:</span>
          {['open', 'in_progress', 'resolved', 'closed'].map(s => (
            <button
              key={s}
              onClick={() => changeStatus(s)}
              disabled={ticket.status === s}
              className={`px-2 py-0.5 rounded text-[11px] ${ticket.status === s ? 'bg-rose-200 text-rose-800 cursor-default' : 'bg-white border border-slate-200 hover:bg-slate-100'}`}
            >
              {STATUS_LABELS[s].label}
            </button>
          ))}
        </div>
      )}

      {ticket.description && (
        <div className="mb-3 p-3 bg-slate-50 rounded text-sm text-slate-700 whitespace-pre-wrap">
          {ticket.description}
        </div>
      )}

      <div className="space-y-2 mb-3">
        {comments.map(c => (
          <div key={c.id} className={`p-2.5 rounded text-sm ${c.is_internal ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'}`}>
            <div className="flex items-center gap-1 text-[11px] text-slate-500 mb-1">
              <span className="font-medium">{c.author_id === ticket.author_id ? 'Zgłaszający' : 'Admin'}</span>
              {c.is_internal && <span className="px-1 py-0.5 bg-amber-100 text-amber-800 rounded">internal</span>}
              <span>•</span>
              <span>{new Date(c.created_at).toLocaleString('pl-PL')}</span>
            </div>
            <div className="text-slate-800 whitespace-pre-wrap">{c.content}</div>
          </div>
        ))}
      </div>

      <form onSubmit={submit} className="space-y-2">
        <textarea
          value={newComment}
          onChange={e => setNewComment(e.target.value)}
          placeholder="Dodaj komentarz..."
          rows={2}
          disabled={sending}
          className="w-full px-3 py-2 border border-slate-200 rounded text-sm resize-none"
        />
        <div className="flex items-center justify-between gap-2">
          {(isAdmin || !isAuthor) && (
            <label className="flex items-center gap-1 text-xs text-amber-700 cursor-pointer">
              <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)} />
              Internal note (niewidoczne dla zgłaszającego)
            </label>
          )}
          <button
            type="submit"
            disabled={!newComment.trim() || sending}
            className="ml-auto flex items-center gap-1 px-3 py-1.5 bg-rose-600 text-white text-sm rounded hover:bg-rose-700 disabled:opacity-50"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Wyślij
          </button>
        </div>
      </form>
    </div>
  );
}

function CreateTicketModal({ communityId, onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('question');
  const [priority, setPriority] = useState('normal');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || saving) return;
    setSaving(true); setError(null);
    try {
      await ticketsApi.create({ community_id: communityId, title: title.trim(), description: description.trim() || undefined, category, priority });
      onCreated();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl max-w-md w-full my-8" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold">Nowy ticket</h2>
          <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Tytuł *</label>
            <input value={title} onChange={e => setTitle(e.target.value)} maxLength={200} required className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Opis (opcjonalnie)</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} maxLength={5000} rows={4} className="w-full px-3 py-2 border border-slate-300 rounded text-sm resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium mb-1">Kategoria</label>
              <select value={category} onChange={e => setCategory(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded text-sm">
                {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Priorytet</label>
              <select value={priority} onChange={e => setPriority(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded text-sm">
                {Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
          </div>
          {error && <p className="text-sm text-rose-600">{error}</p>}
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-slate-200">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded">Anuluj</button>
          <button type="submit" disabled={!title.trim() || saving} className="flex items-center gap-1 px-4 py-2 text-sm bg-rose-600 text-white rounded hover:bg-rose-700 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Utwórz
          </button>
        </div>
      </form>
    </div>
  );
}
